import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, X, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ComposeEmailModal({ inv, onClose, onSent }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [to, setTo] = useState("");
  const [attachment, setAttachment] = useState("");
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (!inv) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/invoices/${inv.id}/email-preview`, {
          withCredentials: true,
          timeout: 10000,
        });
        if (!alive) return;
        setSubject(res.data.subject || "");
        setBody(res.data.html_body || "");
        setTo(res.data.to || "");
        setAttachment(res.data.attachment_filename || "");
        setPreview(false);
      } catch (e) {
        console.error("Preview failed:", e);
        toast.error("Couldn't load email preview.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [inv]);

  if (!inv) return null;

  const send = async () => {
    if (!subject.trim()) {
      toast.error("Subject can't be empty.");
      return;
    }
    if (!body.trim()) {
      toast.error("Body can't be empty.");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/invoices/${inv.id}/send`,
        { subject: subject.trim(), html_body: body },
        { withCredentials: true, timeout: 25000 }
      );
      toast.success(`Sent to ${to}`);
      onSent(res.data);
    } catch (err) {
      console.error("Send failed:", err);
      const detail = err?.response?.data?.detail || "Couldn't send email.";
      toast.error(typeof detail === "string" ? detail : "Couldn't send email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="compose-email-modal"
        className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh]"
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-bold text-slate-900">
              Send {inv.invoice_number}
            </h3>
            <p className="text-xs text-slate-500">
              Review or edit the message before it goes out. The invoice PDF is attached automatically.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="compose-email-close"
            className="text-slate-400 hover:text-slate-700"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {loading ? (
            <div className="py-12 text-slate-500 flex items-center gap-2 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading preview...
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  To
                </label>
                <div
                  data-testid="compose-email-to"
                  className="h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-800 flex items-center"
                >
                  {to}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  data-testid="compose-email-subject"
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm text-slate-900 focus:border-[#1E56B8] focus:ring-0 outline-none"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-slate-600">
                    Body (HTML)
                  </label>
                  <button
                    type="button"
                    onClick={() => setPreview((v) => !v)}
                    data-testid="compose-email-toggle-preview"
                    className="text-xs font-medium text-[#1E56B8] hover:underline"
                  >
                    {preview ? "Edit source" : "Preview"}
                  </button>
                </div>
                {preview ? (
                  <iframe
                    title="Email preview"
                    className="w-full h-72 rounded-lg border border-slate-200 bg-white"
                    sandbox=""
                    srcDoc={body}
                    data-testid="compose-email-preview"
                  />
                ) : (
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    data-testid="compose-email-body"
                    rows={12}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-xs text-slate-900 font-mono focus:border-[#1E56B8] focus:ring-0 outline-none"
                  />
                )}
                <p className="mt-1 text-[11px] text-slate-400">
                  You can safely edit the wording. Click <em>Preview</em> to see it as the customer will.
                </p>
              </div>
              {attachment && (
                <div className="inline-flex items-center gap-1.5 text-xs text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-3 py-1">
                  <FileText className="w-3.5 h-3.5" />
                  Attaching {attachment}
                </div>
              )}
            </>
          )}
        </div>

        <footer className="px-6 py-4 border-t border-slate-200 flex flex-wrap justify-end gap-2 bg-slate-50 rounded-b-2xl">
          <button
            onClick={onClose}
            data-testid="compose-email-cancel"
            className="h-10 px-4 rounded-lg text-sm text-slate-600 hover:bg-slate-100 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={send}
            disabled={loading || sending}
            data-testid="compose-email-send"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[#1E56B8] hover:bg-[#2E6BD9] text-white text-sm font-semibold disabled:opacity-60"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send email
          </button>
        </footer>
      </div>
    </div>
  );
}
